/**
 * PATH: apps/billing/src/pages/BillsPage.tsx
 * FILE_ID: BILLING-PAGE-BILLS-001
 * PURPOSE: Bills list table for the active tenant.
 * WHY: Was rendering a hard-coded mockBills array. Now live-wired to
 *      /api/bills/ via useBills(). Mock fallback gated behind DEV_DEMO=1.
 *
 * FLOW:
 *   useBills(filter) -> /api/bills/ -> BillListRow[]
 *     -> mapped to display row -> rendered
 *
 * DEPENDENCIES:
 *   ../hooks/useBills: live data + mutations (approve, reject, push)
 *
 * RELATED:
 *   docs/architecture/billing/00_MASTER_PLAN.md § Phase A: this is the file
 *   that closes Phase A's "list page renders real data" deliverable.
 *   apps/sam/src/features/bills/pages/BillsPage.tsx: the SAM equivalent;
 *   in Phase B both will share a packaged component.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Filter,
  Upload,
  MoreHorizontal,
  Eye,
  Edit,
  Send,
  CheckCircle,
  Clock,
  AlertTriangle,
  FileText,
  Loader2,
  RefreshCcw,
} from 'lucide-react';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import {
  useBills,
  useExtractPendingBills,
  type BillListRow,
  type BillStatus,
} from '../hooks/useBills';

// ---------------------------------------------------------------------------
// Display config — maps production BillStatus values to label/colour/icon.
// Backend emits: pending | extracted | reviewed | pushed | failed.
// ---------------------------------------------------------------------------

const statusConfig: Record<BillStatus, { label: string; color: string; icon: typeof FileText }> = {
  pending: {
    label: 'Pending extraction',
    color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
    icon: FileText,
  },
  extracted: {
    label: 'AI extracted',
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
    icon: Clock,
  },
  reviewed: {
    label: 'Reviewed',
    color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
    icon: CheckCircle,
  },
  pushed: {
    label: 'Pushed to Xero',
    color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
    icon: Send,
  },
  failed: {
    label: 'Extraction failed',
    color: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
    icon: AlertTriangle,
  },
  quarantined: {
    label: 'Quarantined',
    color: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
    icon: AlertTriangle,
  },
  awaiting_document: {
    label: 'Awaiting document',
    color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
    icon: Clock,
  },
  needs_fetch: {
    label: 'Needs fetch',
    color: 'bg-slate-100 text-slate-800 dark:bg-slate-800/40 dark:text-slate-200',
    icon: Clock,
  },
  needs_review: {
    label: 'Needs review',
    color: 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/40 dark:text-fuchsia-200',
    icon: AlertTriangle,
  },
};

// ---------------------------------------------------------------------------
// Pure helpers — small enough to keep co-located but pure for testability.
// ---------------------------------------------------------------------------

function parseAmount(v: string | null): number | null {
  if (v == null) return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

function confidencePct(raw: string | null): number | null {
  // Backend emits 0.000-1.000 (DecimalField, max 4 digits, 3 decimal places).
  // UI shows 0-100. Null => unextracted.
  if (raw == null) return null;
  const n = parseFloat(raw);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

interface DisplayBill {
  id: string;
  vendor: string;
  invoiceNumber: string;
  amountFmt: string;
  amountValue: number | null;
  dueDateFmt: string;
  status: BillStatus;
  confidencePct: number | null;
  pushedToXero: boolean;
}

function toDisplayBill(b: BillListRow): DisplayBill {
  const amount = parseAmount(b.total);
  return {
    id: b.id,
    vendor: b.vendor_name || b.email_from || '(unknown vendor)',
    invoiceNumber: b.invoice_number || '—',
    amountFmt: amount != null ? formatCurrency(amount) : '—',
    amountValue: amount,
    dueDateFmt: b.due_date ? formatDate(b.due_date) : '—',
    status: b.status,
    confidencePct: confidencePct(b.extraction_confidence),
    pushedToXero: !!b.xero_invoice_id,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function BillsPage() {
  const [statusFilter, setStatusFilter] = useState<BillStatus | 'all'>('all');
  const [search, setSearch] = useState('');
  const [selectedBills, setSelectedBills] = useState<Set<string>>(new Set());

  const { data, isLoading, error, refetch, isFetching } = useBills({ status: statusFilter });
  const extractPending = useExtractPendingBills();

  const rawBills: BillListRow[] = data?.bills ?? [];
  const filteredBills = search.trim()
    ? rawBills.filter((b) => {
        const q = search.trim().toLowerCase();
        return (
          b.vendor_name.toLowerCase().includes(q) ||
          b.invoice_number.toLowerCase().includes(q) ||
          b.email_subject.toLowerCase().includes(q)
        );
      })
    : rawBills;

  const displayBills = filteredBills.map(toDisplayBill);

  const toggleSelection = (id: string) => {
    setSelectedBills((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedBills((prev) =>
      prev.size === displayBills.length ? new Set() : new Set(displayBills.map((b) => b.id)),
    );
  };

  const handleExtractPending = () => {
    extractPending.mutate({ limit: 50 });
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Bills</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {data?.counts.total ?? 0} bills total
            {statusFilter !== 'all' && ` (${displayBills.length} in ${statusConfig[statusFilter]?.label ?? statusFilter})`}
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          aria-label="Refresh bills"
        >
          <RefreshCcw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {/* Status filter chips — uses the actual production statuses */}
      <div className="flex flex-wrap items-center gap-2">
        {(['all', 'pending', 'extracted', 'reviewed', 'pushed', 'failed'] as const).map((s) => {
          const count =
            s === 'all' ? data?.counts.total ?? 0 : data?.counts.by_status?.[s] ?? 0;
          const isActive = statusFilter === s;
          return (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full border transition',
                isActive
                  ? 'bg-emerald-600 border-emerald-600 text-white'
                  : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700',
              )}
            >
              <span className="capitalize">{s === 'all' ? 'All' : statusConfig[s]?.label ?? s}</span>
              <span
                className={cn(
                  'text-xs',
                  isActive ? 'text-white/80' : 'text-gray-500 dark:text-gray-400',
                )}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Actions Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search vendor, invoice number, subject..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-80 pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
            <Filter className="h-4 w-4" />
            More filters
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExtractPending}
            disabled={extractPending.isPending}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            title="Run AI extraction on every pending row"
          >
            {extractPending.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            Extract pending
          </button>
          <button
            disabled={selectedBills.size === 0}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
            Push to Xero ({selectedBills.size})
          </button>
        </div>
      </div>

      {/* Bills Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <div className="p-12 flex items-center justify-center text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading bills…
          </div>
        ) : error ? (
          <div className="p-12 text-center">
            <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />
            <p className="text-sm text-red-600 dark:text-red-400">
              Couldn't load bills: {error.message}
            </p>
            <button
              onClick={() => refetch()}
              className="mt-4 px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
            >
              Retry
            </button>
          </div>
        ) : displayBills.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {search || statusFilter !== 'all'
                ? 'No bills match the current filter.'
                : 'No bills yet. Forward a supplier invoice to your Outlook inbox or upload one.'}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="w-12 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedBills.size > 0 && selectedBills.size === displayBills.length}
                    onChange={toggleAll}
                    className="h-4 w-4 rounded border-gray-300"
                    aria-label="Select all bills"
                  />
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                  Vendor
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                  Invoice #
                </th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">
                  Amount
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                  Due Date
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                  Confidence
                </th>
                <th className="w-24 px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {displayBills.map((bill) => {
                const statusInfo = statusConfig[bill.status];
                const StatusIcon = statusInfo?.icon ?? FileText;
                return (
                  <tr
                    key={bill.id}
                    className={cn(
                      'hover:bg-gray-50 dark:hover:bg-gray-700',
                      selectedBills.has(bill.id) && 'bg-emerald-50 dark:bg-emerald-900/20',
                    )}
                  >
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedBills.has(bill.id)}
                        onChange={() => toggleSelection(bill.id)}
                        className="h-4 w-4 rounded border-gray-300"
                        aria-label={`Select ${bill.vendor}`}
                      />
                    </td>
                    <td className="px-4 py-4">
                      <Link
                        to={`/bills/${bill.id}`}
                        className="font-medium text-gray-900 dark:text-white hover:text-emerald-600"
                      >
                        {bill.vendor}
                      </Link>
                    </td>
                    <td className="px-4 py-4 text-gray-600 dark:text-gray-400">
                      {bill.invoiceNumber}
                    </td>
                    <td className="px-4 py-4 text-right font-medium text-gray-900 dark:text-white">
                      {bill.amountFmt}
                    </td>
                    <td className="px-4 py-4 text-gray-600 dark:text-gray-400">
                      {bill.dueDateFmt}
                    </td>
                    <td className="px-4 py-4">
                      <span
                        className={cn(
                          'px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-fit',
                          statusInfo?.color ?? 'bg-gray-100 text-gray-800',
                        )}
                      >
                        <StatusIcon className="h-3 w-3" />
                        {statusInfo?.label ?? bill.status}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      {bill.confidencePct == null ? (
                        <span className="text-gray-400 text-xs">—</span>
                      ) : (
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-2 bg-gray-100 dark:bg-gray-600 rounded-full overflow-hidden">
                            <div
                              className={cn(
                                'h-full rounded-full',
                                bill.confidencePct >= 90
                                  ? 'bg-emerald-500'
                                  : bill.confidencePct >= 70
                                    ? 'bg-amber-500'
                                    : 'bg-red-500',
                              )}
                              style={{ width: `${bill.confidencePct}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {bill.confidencePct}%
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1">
                        <Link
                          to={`/bills/${bill.id}`}
                          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600"
                          aria-label="View bill"
                        >
                          <Eye className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                        </Link>
                        <Link
                          to={`/bills/${bill.id}?edit=1`}
                          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600"
                          aria-label="Edit bill"
                        >
                          <Edit className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                        </Link>
                        <button
                          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600"
                          aria-label="More actions"
                        >
                          <MoreHorizontal className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {extractPending.isError && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300">
          Couldn't queue extraction: {extractPending.error.message}
        </div>
      )}
      {extractPending.isSuccess && extractPending.data && (
        <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 p-3 text-sm text-emerald-700 dark:text-emerald-300">
          Queued {extractPending.data.queued} email
          {extractPending.data.queued === 1 ? '' : 's'} for AI extraction.
          {extractPending.data.skipped_no_attachment > 0 &&
            ` Skipped ${extractPending.data.skipped_no_attachment} (no attachment).`}
        </div>
      )}
    </div>
  );
}
